import { Card } from "$root/components/EvolvedPrint/components/Card";
import { commonReportColumns } from "$root/components/EvolvedPrint/components/table/common-report-columns";
import type { PrintableProps } from "$root/components/EvolvedPrint/configuration/types";
import { type TableColumn, BaseTable } from "@mdotm/shadcn/legacy/components";
import { useMemo } from "react";

/**
 * Flat (already bucket-filtered) row consumed by the print pipeline. Each row carries the
 * label of the time-period bucket it belongs to, shown as a column — group banding is encoded
 * per row rather than via spanning header rows so pagination can split a single flat list.
 */
export type MaturityCouponPrintRow = {
	bucketShortName: string;
	instrumentName: string;
	tickerName: string;
	/** Expiration date (maturity) or coupon date (coupon). ISO string. */
	date: string | null;
	/** Only meaningful for the coupon section. */
	couponRate?: number | null;
};

type MaturityCouponColumn = Omit<TableColumn<MaturityCouponPrintRow>, "width"> & { width: number };

const bucketColumn: MaturityCouponColumn = {
	...commonReportColumns.string<MaturityCouponPrintRow>("bucketShortName", "Bucket"),
	width: 64,
	maxWidth: 64,
};

const instrumentColumns: MaturityCouponColumn[] = [
	{
		...commonReportColumns.string<MaturityCouponPrintRow>("instrumentName", "Instrument"),
		minWidth: 160,
		width: 160,
		headerCellStyle: { flexGrow: 1 },
		cellStyle: { flexGrow: 1 },
	},
	{
		...commonReportColumns.string<MaturityCouponPrintRow>("tickerName", "Ticker"),
		width: 124,
		maxWidth: 124,
	},
];

const dateColumn = (header: string): MaturityCouponColumn => ({
	...commonReportColumns.date<MaturityCouponPrintRow>("date", header),
	width: 110,
	maxWidth: 110,
});

const couponRateColumn: MaturityCouponColumn = {
	...commonReportColumns.percentage<MaturityCouponPrintRow>("couponRate", "Rate / Yield"),
	width: 90,
	maxWidth: 90,
};

const MaturityCouponTracker = ({
	sectionTitle,
	section,
	list,
}: PrintableProps<
	{ sectionTitle: string; section: "maturity" | "coupon" },
	MaturityCouponPrintRow
>): React.JSX.Element => {
	// Memoized so the array reference stays stable across renders, preventing table reflows.
	const columns = useMemo<MaturityCouponColumn[]>(() => {
		// Hide the date column entirely when no instrument in the section carries a date.
		const hasAnyDate = list.some((row) => row.date != null);
		const dateHeader = section === "maturity" ? "Expiration date" : "Coupon date";
		return [
			bucketColumn,
			...instrumentColumns,
			...(hasAnyDate ? [dateColumn(dateHeader)] : []),
			...(section === "coupon" ? [couponRateColumn] : []),
		];
	}, [list, section]);

	return (
		<Card title={sectionTitle}>
			<BaseTable disableVirtualScroll palette="uniform" columns={columns} rows={list} noDataText="No data available" />
		</Card>
	);
};

export default MaturityCouponTracker;
